/* mm-video-row: lazy muted tiles + native fullscreen story player with add to cart. */
(function () {
  if (customElements.get('mm-video-row')) return;

  class MMVideoRow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.mm-vrow__track');
      this.tiles = Array.from(this.querySelectorAll('.mm-vrow__open'));
      this.autoplay = this.dataset.autoplay || 'in_view';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Shopify often keeps a single full size mp4 per clip (several MB). Where the browser plays HLS itself
      // (iPhone, most Android), use the adaptive stream instead: it starts on a light rendition right away.
      this.nativeHls = !!document.createElement('video').canPlayType('application/vnd.apple.mpegurl');
      this.player = null;
      this.index = 0;

      this.tiles.forEach((btn) => {
        btn.addEventListener('click', () => this.open(Number(btn.dataset.index)));
        if (this.autoplay === 'hover') {
          btn.addEventListener('pointerenter', () => this.playTile(btn));
          btn.addEventListener('pointerleave', () => this.pauseTile(btn));
        }
      });

      if (this.autoplay === 'in_view' && !this.reduceMotion && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => entries.forEach((e) => (e.isIntersecting ? this.playTile(e.target) : this.pauseTile(e.target))),
          { threshold: 0.35 }
        );
        this.tiles.forEach((btn) => this.io.observe(btn));
        // Start buffering a tile a little before it scrolls into view, so it plays the moment it shows.
        this.warm = new IntersectionObserver(
          (entries) =>
            entries.forEach((e) => {
              if (!e.isIntersecting) return;
              const v = e.target.querySelector('video');
              if (v && !v.src) {
                v.preload = 'auto';
                this.load(v, v.dataset);
              }
              this.warm.unobserve(e.target);
            }),
          { rootMargin: '600px 0px' }
        );
        this.tiles.forEach((btn) => this.warm.observe(btn));
      }

      this.querySelectorAll('.mm-vrow__arrow').forEach((a) => {
        a.addEventListener('click', () => this.scrollBy(Number(a.dataset.dir)));
      });
      if (this.track) {
        this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
        window.addEventListener('resize', () => this.updateArrows());
        this.updateArrows();
      }
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.warm) this.warm.disconnect();
      this.close();
    }

    source(el) {
      return (this.nativeHls && el.dataset.hls) || el.dataset.src;
    }
    // Point a video at its stream. If the browser claimed HLS support but cannot play it, fall back to the mp4 once.
    load(video, data) {
      const hls = this.nativeHls && data.hls;
      video.src = hls || data.src;
      if (!hls) return;
      video.addEventListener(
        'error',
        () => {
          video.src = data.src;
          const play = video.play();
          if (play && play.catch) play.catch(() => {});
        },
        { once: true }
      );
    }

    /* ---- tiles ---- */
    playTile(btn) {
      const v = btn.querySelector('video');
      if (!v) return;
      if (!v.src) this.load(v, v.dataset);
      v.muted = true;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
      btn.classList.add('is-playing');
    }
    pauseTile(btn) {
      const v = btn.querySelector('video');
      if (!v || !v.src) return;
      v.pause();
      btn.classList.remove('is-playing');
    }
    pauseAllTiles() {
      this.tiles.forEach((b) => this.pauseTile(b));
    }
    resumeTiles() {
      if (this.autoplay !== 'in_view' || this.reduceMotion) return;
      this.tiles.forEach((b) => {
        const r = b.getBoundingClientRect();
        if (r.bottom > 0 && r.top < window.innerHeight && r.right > 0 && r.left < window.innerWidth) this.playTile(b);
      });
    }
    scrollBy(dir) {
      const first = this.track.querySelector('.mm-vrow__tile');
      const step = first ? first.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).gap || 8) : 160;
      this.track.scrollBy({ left: dir * step * 2, behavior: 'smooth' });
    }
    updateArrows() {
      const prev = this.querySelector('.mm-vrow__arrow--prev');
      const next = this.querySelector('.mm-vrow__arrow--next');
      if (!prev || !next) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      prev.hidden = this.track.scrollLeft <= 2;
      next.hidden = max <= 2 || this.track.scrollLeft >= max - 2;
    }

    /* ---- player ---- */
    open(index) {
      if (this.player) return;
      const tpl = this.querySelector('.mm-vrow__player-template');
      if (!tpl) return;
      this.player = tpl.content.firstElementChild.cloneNode(true);
      this.video = this.player.querySelector('.mm-player__video');
      this.bar = this.player.querySelector('.mm-player__progress-bar');
      this.muteBtn = this.player.querySelector('[data-mute]');
      this.lastFocus = document.activeElement;
      this.pauseAllTiles();

      this.player.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => this.close()));
      this.player.querySelectorAll('[data-nav]').forEach((el) =>
        el.addEventListener('click', () => this.go(this.index + Number(el.dataset.nav)))
      );
      this.player.querySelector('[data-toggle]').addEventListener('click', () => this.togglePlay());
      this.muteBtn.addEventListener('click', () => this.toggleMute());
      const atc = this.player.querySelector('[data-atc]');
      if (atc) atc.addEventListener('click', () => this.addToCart());

      this.video.addEventListener('timeupdate', () => {
        if (this.video.duration) this.bar.style.width = (this.video.currentTime / this.video.duration) * 100 + '%';
      });
      this.video.addEventListener('ended', () => this.go(this.index + 1, true));
      this.video.addEventListener('play', () => this.player.classList.remove('is-paused'));
      this.video.addEventListener('pause', () => this.player.classList.add('is-paused'));

      this.onKey = (e) => {
        if (e.key === 'Escape') this.close();
        else if (e.key === 'ArrowRight') this.go(this.index + 1);
        else if (e.key === 'ArrowLeft') this.go(this.index - 1);
        else if (e.key === ' ') { e.preventDefault(); this.togglePlay(); }
      };
      document.addEventListener('keydown', this.onKey);
      this.bindSwipe();

      document.body.appendChild(this.player);
      document.body.classList.add('mm-player-open');
      this.muted = false;
      this.go(index);
      this.player.querySelector('.mm-player__close').focus();
    }

    close() {
      if (!this.player) return;
      document.removeEventListener('keydown', this.onKey);
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
      this.player.remove();
      this.player = null;
      document.body.classList.remove('mm-player-open');
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
      this.resumeTiles();
    }

    go(index, fromEnded) {
      const count = this.tiles.length;
      if (index < 0 || index >= count) {
        if (fromEnded) { this.video.currentTime = 0; this.video.play().catch(() => {}); }
        return;
      }
      this.index = index;
      const d = this.tiles[index].dataset;
      const p = this.player;

      p.querySelector('.mm-player__nav--prev').disabled = index === 0;
      p.querySelector('.mm-player__nav--next').disabled = index === count - 1;
      p.querySelector('.mm-player__caption').textContent = d.caption || '';

      const prod = p.querySelector('.mm-player__product');
      if (prod) {
        prod.querySelector('.mm-player__product-img').src = d.productImage || '';
        const title = prod.querySelector('.mm-player__product-title');
        title.textContent = d.productTitle || '';
        title.href = d.productUrl || '#';
        prod.querySelector('.mm-player__product-amount').textContent = d.productPrice || '';
        const cmp = prod.querySelector('.mm-player__product-compare');
        cmp.hidden = !d.productCompare;
        cmp.textContent = d.productCompare || '';
        const atc = prod.querySelector('[data-atc]');
        atc.disabled = 'soldOut' in d;
        atc.classList.remove('is-loading');
        const label = atc.querySelector('.mm-player__atc-label');
        if (!atc.dataset.label) atc.dataset.label = label.textContent;
        label.textContent = 'soldOut' in d ? (window.variantStrings && variantStrings.soldOut) || 'Sold out' : atc.dataset.label;
      }

      this.bar.style.width = '0%';
      this.video.poster = d.poster || '';
      this.load(this.video, d);
      this.video.muted = this.muted;
      this.muteBtn.setAttribute('aria-pressed', String(this.muted));
      const play = this.video.play();
      if (play && play.catch) {
        play.catch(() => {
          // Autoplay with sound refused: fall back to muted so the story still runs.
          this.muted = true;
          this.video.muted = true;
          this.muteBtn.setAttribute('aria-pressed', 'true');
          this.video.play().catch(() => {});
        });
      }
      // Warm the next clip.
      const next = this.tiles[index + 1];
      if (next && !this._warm) {
        this._warm = document.createElement('link');
        this._warm.rel = 'preload';
        this._warm.as = 'video';
        document.head.appendChild(this._warm);
      }
      if (next && this._warm && !(this.nativeHls && next.dataset.hls)) this._warm.href = next.dataset.src;
    }

    togglePlay() {
      if (this.video.paused) this.video.play().catch(() => {});
      else this.video.pause();
    }
    toggleMute() {
      this.muted = !this.muted;
      this.video.muted = this.muted;
      this.muteBtn.setAttribute('aria-pressed', String(this.muted));
    }

    bindSwipe() {
      let x0 = null, y0 = null;
      const card = this.player.querySelector('.mm-player__card');
      card.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
      card.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        const dy = e.changedTouches[0].clientY - y0;
        x0 = y0 = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) this.go(this.index + (dx < 0 ? 1 : -1));
        else if (dy > 90 && Math.abs(dy) > Math.abs(dx)) this.close();
      }, { passive: true });
    }

    /* ---- cart ---- */
    addToCart() {
      const d = this.tiles[this.index].dataset;
      const atc = this.player.querySelector('[data-atc]');
      if (!d.variantId || atc.disabled) return;
      // Monster Cart Upsell replaces the theme cart on this store: add through it and open its drawer.
      const monster = document.querySelector('#monster-cart-wrapper');
      if (monster && typeof window.monster_atc === 'function') {
        atc.classList.add('is-loading');
        atc.disabled = true;
        Promise.resolve(window.monster_atc({ id: Number(d.variantId), quantity: 1 }))
          .then(() => {
            this.close();
            if (!monster.classList.contains('mu_openned')) {
              const bag = document.querySelector('#cart-icon-bubble');
              if (bag) bag.click();
            }
          })
          .catch(() => {
            atc.classList.remove('is-loading');
            atc.disabled = false;
            this.flash(atc, 'Could not add');
          });
        return;
      }
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      // Single-item shape so the response carries the line item key Dawn's cart UI expects.
      const body = { id: Number(d.variantId), quantity: 1 };
      if (cart && cart.getSectionsToRender) {
        body.sections = cart.getSectionsToRender().map((s) => s.id).join(',');
        body.sections_url = window.location.pathname;
      }
      atc.classList.add('is-loading');
      atc.disabled = true;
      fetch((window.routes && routes.cart_add_url) || '/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      })
        .then((r) => r.json())
        .then((res) => {
          atc.classList.remove('is-loading');
          atc.disabled = false;
          if (res.status) {
            this.flash(atc, res.description || res.message || 'Could not add');
            return;
          }
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mm-video-row', productVariantId: d.variantId, cartData: res });
          }
          if (cart && cart.renderContents) {
            this.close();
            try { cart.renderContents(res); } catch (e) { /* cart UI out of sync, the item is still in the cart */ }
          } else {
            this.flash(atc, this.dataset.cartMessage || 'Added');
          }
        })
        .catch(() => {
          atc.classList.remove('is-loading');
          atc.disabled = false;
          this.flash(atc, 'Could not add');
        });
    }
    flash(atc, text) {
      const label = atc.querySelector('.mm-player__atc-label');
      const orig = atc.dataset.label || label.textContent;
      label.textContent = text;
      setTimeout(() => { label.textContent = orig; }, 1800);
    }
  }

  customElements.define('mm-video-row', MMVideoRow);
})();
